"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useCustomer } from "@/hooks/useCustomer";
import { Locales } from "@/lib/i18n";
import { toast } from "@/components/atoms/toast";
import { __, getLoginRedirectUrl } from "@/lib/i18n/functions";
import { consumeVoluntaryLogout } from "@/lib/auth/logout-intent";
import { getHomePageSlug } from "@/lib/utils";

type AccountRouteGuardProps = {
  children: React.ReactNode;
  locale: Locales;
  allowedPublicRoutes?: string[];
  route: string;
};

export const AccountRouteGuard = ({ children, locale, route, allowedPublicRoutes = [] }: AccountRouteGuardProps) => {
  const pathname = usePathname();
  const { customer } = useCustomer();
  const router = useRouter();

  useEffect(() => {
    // Wait for customer state to be loaded
    if (customer === undefined) return;

    const isLoggedIn = customer !== null;
    const isPublic = allowedPublicRoutes.includes(route);

    if (!isLoggedIn && !isPublic) {
      if (consumeVoluntaryLogout()) {
        router.push(getHomePageSlug(locale));
        return;
      }

      toast.error({
        title: __("Please log in to access your account", locale),
      });

      const redirectUrl = getLoginRedirectUrl(locale, route, "login");
      router.push(`${redirectUrl}?redirect=${pathname}`);
    }
  }, [customer, router, locale, route, pathname, allowedPublicRoutes]);

  // Show loading while customer state is loading
  if (customer === undefined) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="border-dark-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  const isLoggedIn = customer !== null;
  const isPublic = allowedPublicRoutes.includes(route);

  if (!isLoggedIn && !isPublic) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="border-dark-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  return <>{children}</>;
};
